import React, { createContext, useState, useEffect, useContext } from "react";
import { getUserData, login as loginService, logout as logoutService } from "../services/authService";

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [authLoaded, setAuthLoaded] = useState(false);

    useEffect(() => {
        const initializeAuth = async () => {
            const token = localStorage.getItem("access_token");
            if (!token) {
                setLoading(false);
                setAuthLoaded(true);
                return;
            }

            try {
                // 由於現在有interceptor自動處理token刷新
                // 這裡只需要嘗試獲取用戶數據，interceptor會處理401錯誤
                const userData = await getUserData();
                if (userData.success) {
                    setUser(userData.data);
                    console.log("✅ Auth initialized successfully, user:", userData.data);
                } else {
                    console.warn("⚠️ Failed to get user data:", userData.error);
                    logoutService();
                }
            } catch (error) {
                console.error("初始化驗證失敗，清除無效token:", error);
                // 如果interceptor也無法處理，說明token已完全失效
                logoutService();
            } finally {
                setLoading(false);
                setAuthLoaded(true);
            }
        };

        initializeAuth();
    }, []);

    const handleLogin = async (username, password) => {
        try {
            const loginResult = await loginService(username, password);
            if (loginResult.success) {
                const userData = await getUserData();
                if (userData.success) {
                    setUser(userData.data);
                    console.log("✅ Login successful, user:", userData.data);
                } else {
                    throw new Error(userData.error || "獲取用戶資料失敗");
                }
            } else {
                throw new Error(loginResult.error || "登入失敗");
            }
        } catch (error) {
            console.error("🛑 Login failed:", error);
            alert("登入失敗：" + (error.response?.data?.detail || error.message));
            throw error;
        }
    };

    const handleLogout = () => {
        logoutService();
        setUser(null);
    };

    const refreshUser = async () => {
        try {
            const result = await getUserData();
            if (result.success) {
                setUser(result.data);
            }
            return result;
        } catch (error) {
            console.error("刷新用戶資料失敗:", error);
            return {
                success: false,
                error: error.message,
            };
        }
    };

    return (
        <AuthContext.Provider
            value={{
                user,
                login: handleLogin,
                logout: handleLogout,
                loading,
                authLoaded,
                isAuthenticated: !!user,
                refreshUser,
            }}
        >
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => useContext(AuthContext);
